// Stammdaten-Tab: die oeffentliche Vario-Domain des Mandanten.
//
// WOZU DAS DA IST (2026-08-07, gemessen, nicht vermutet):
// Kaufland holt Produktbilder selbst ab. Der Link, den wir mitschicken, entstand bisher aus
// dem Host, den wir aus dem Token-Realm ableiten -- und der antwortet auf der OEFFENTLICHEN
// DMS-Route mit HTTP 500 "can't determine tenant from url". Dieselbe Datei unter der
// Kundendomain liefert 200 und `image/jpeg`, gleicher Hash, gleiche 109143 Bytes. Kaufland
// quittiert den kaputten Link mit `picture: DECLINED, reason: media_not_downloadable` und
// gibt das Produkt nicht frei. Das trifft ALLE Mandanten: beide verbundenen tragen einen
// technischen Realm.
//
// Es gibt keinen Endpunkt, der die oeffentliche Domain liefert. Die lokale OpenAPI kennt
// weder Mandanten- noch Instanz-Endpunkt, und das Developer-Portal sagt woertlich: "Es gibt
// keinen expliziten API-Endpunkt, der die oeffentliche Domain zurueckgibt." Vario nennt als
// Weg, sie aus der URL abzuleiten, unter der der Kunde die Installation aufruft -- und liest
// sie im eigenen App-Framework (`frontend/script/secure.js`) aus `document.referrer`.
//
// Deshalb ZWEI Wege, und bewusst beide:
//   * automatisch aus `document.referrer` (captureVarioHost, laeuft in JEDEM Slot),
//   * hier sichtbar und ueberschreibbar.
// Der zweite ist nicht Zierde: Genau eine still danebenliegende Annahme ueber diesen Host
// hat die erste Produktanlage gekostet. Was der Push benutzt, gehoert vor Augen.

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';

interface Ctx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind: string };
  supabaseUrl: string;
}

/**
 * Die Regel, welcher Referrer als Mandanten-Domain zaehlt. Bewusst DOM-frei, damit sie
 * testbar ist (Muster: `lib/publish-button.ts`, `worker/lib/price-diff.ts`) -- der DOM-Zugriff
 * steckt in `readEmbeddingHost` daneben.
 *
 * Der Muster-Satz ist derselbe wie in Varios eigenem `secure.js`: `<host>.vario.cloud` plus
 * die stage- und dev-Varianten. Er ist kein Formalismus: Der Referrer ist eine Angabe des
 * Browsers, nicht unsere. Ohne den Riegel koennte eine fremde Einbettung den Bild-Link eines
 * Mandanten auf ihre Domain umbiegen. Die RPC prueft dasselbe noch einmal serverseitig --
 * diese Pruefung hier ist die schnelle, nicht die verlaessliche.
 *
 * ZWEITER RIEGEL (2026-08-11): der technische Realm-Host wird abgewiesen, siehe
 * TECHNICAL_REALM_HOST.
 */
export function parseEmbeddingHost(referrer: string | null, embedded: boolean): string | null {
  // Ohne Einbettung gibt es keine Vario-Oberflaeche darueber; der Referrer waere dann
  // bestenfalls unsere eigene Adresse.
  if (!embedded) return null;
  if (!referrer) return null;
  try {
    const url = new URL(referrer);
    if (!/^[a-z0-9][a-z0-9-]*\.(dev-|stage-)?vario\.cloud$/i.test(url.hostname)) return null;
    if (isTechnicalRealmHost(url.hostname)) return null;
    return `https://${url.hostname.toLowerCase()}`;
  } catch {
    return null;
  }
}

/**
 * Der Host aus dem Token-Realm, Form `tn` + Mandantennummer (`tn0000001529.vario.cloud`).
 *
 * Warum er hier ABGEWIESEN wird, obwohl er eine gueltige Vario-Cloud-Domain ist: Er ist genau
 * der Wert, den diese Spalte ersetzen soll. Auf der oeffentlichen DMS-Route antwortet er mit
 * HTTP 500 ("can't determine tenant from url"); dieselbe Datei unter der Kundendomain liefert
 * 200 und `image/jpeg` (gemessen 2026-08-08). Als gespeicherte Domain ist er deshalb nie
 * richtig -- er ist die Krankheit, nicht die Heilung.
 *
 * Der Vorfall vom 2026-08-11: Ein Haendler rief die App einmal unter dem technischen Host auf,
 * `captureVarioHost` legte ihn still ab, und ab da schrieb `rewritePublicShareHost` jede
 * korrekt gespeicherte Bild-URL auf ihn um. Im Lister standen wieder kaputte Bilder -- erzeugt
 * von genau der Mechanik, die sie verhindern sollte. Beide Riegel liessen ihn durch, weil
 * beide nur nach "fremde Domain" suchten.
 *
 * Bewusst eng: `tn` gefolgt von AUSSCHLIESSLICH Ziffern. `tnt.vario.cloud` oder
 * `tn-handel.vario.cloud` waeren echte Kundendomains und muessen durchkommen.
 */
const TECHNICAL_REALM_HOST = /^tn\d+\.(dev-|stage-)?vario\.cloud$/i;

export function isTechnicalRealmHost(hostname: string): boolean {
  return TECHNICAL_REALM_HOST.test(hostname);
}

/**
 * Liest den Host, in dem diese App als iframe steckt.
 *
 * `document.referrer` ist die Adresse der EINBETTENDEN Seite, also der Vario-Oberflaeche des
 * Kunden -- dieselbe Quelle, aus der Varios eigenes App-Framework den Host liest.
 */
export function readEmbeddingHost(): string | null {
  return parseEmbeddingHost(document.referrer, window.parent !== window);
}

/**
 * Warum `parseEmbeddingHost` nichts geliefert hat -- nur fuer die Beschriftung im Tab.
 *
 * Ohne diese Unterscheidung saehe ein Haendler, der Vario unter dem technischen Host aufruft,
 * seit 0045 den Satz "Nur moeglich, wenn die App in Vario geoeffnet ist" -- waehrend sie in
 * Vario offen ist. Der Riegel waere richtig und die Begruendung daneben.
 */
export function embeddingHostProblem(
  referrer: string | null,
  embedded: boolean,
): 'ok' | 'nicht-eingebettet' | 'technischer-host' {
  if (parseEmbeddingHost(referrer, embedded) !== null) return 'ok';
  if (!embedded) return 'nicht-eingebettet';
  try {
    if (referrer && isTechnicalRealmHost(new URL(referrer).hostname)) return 'technischer-host';
  } catch {
    // faellt unten durch
  }
  return 'nicht-eingebettet';
}

/**
 * Erfasst die Domain einmal je Slot-Aufruf, still.
 *
 * Absichtlich fire-and-forget und ohne jede Fehlermeldung: Das hier ist ein Nebenzweck jedes
 * Aufrufs, kein Auftrag des Haendlers. Schlaegt es fehl, bleibt der bisherige Wert stehen und
 * der Stammdaten-Tab zeigt ihn.
 *
 * Schreibt nur, wenn sich etwas aendert -- sonst laeuft bei jedem Tab-Wechsel ein UPDATE.
 */
export async function captureVarioHost(supabase: SupabaseClient): Promise<void> {
  const host = readEmbeddingHost();
  if (!host) return;
  try {
    const { data } = await supabase.rpc('kaufland_get_vario_public_base_url');
    const stored = Array.isArray(data) ? (data[0]?.public_base_url ?? null) : null;
    if (stored === host) return;
    await supabase.rpc('kaufland_set_vario_public_base_url', { _url: host });
  } catch {
    // still, siehe Kommentar oben.
  }
}

export function renderMasterdataSlot(ctx: Ctx): void {
  // Rahmen wortgleich zum MCP- und Einstellungen-Tab: volle iframe-Hoehe, Scrolling innen.
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="kf-page" style="height:100%;display:flex;flex-direction:column;">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
          <span class="hdivider"></span>
          <div class="htitle"><div class="heyebrow">Kaufland · Stammdaten</div><h1>Angaben dieses Mandanten</h1></div>
        </header>
        <div class="scrollarea">
          <section class="kl-card" style="max-width:720px;">
            <h3 class="kl-card-title">Vario-Domain</h3>
            <p class="kl-muted" style="margin:0 0 12px;">
              Unter dieser Adresse ruft Kaufland Ihre Produktbilder ab. Sie wird automatisch
              erkannt, sobald Sie diese App in Vario öffnen. Stimmt sie nicht, können Sie sie
              hier korrigieren.
            </p>
            <div id="md-derived" class="kl-muted" style="margin:0 0 12px;font-size:12px;"></div>
            <label class="kl-label" for="md-url">Öffentliche Vario-Domain</label>
            <input id="md-url" class="kl-input" type="text" placeholder="https://kunde.vario.cloud"
                   autocomplete="off" spellcheck="false" style="max-width:420px;" />
            <div id="md-hint" class="kl-muted" style="margin-top:8px;font-size:12px;"></div>
            <div style="margin-top:16px;display:flex;gap:8px;align-items:center;">
              <button id="md-save" class="btn btn-primary" type="button">Speichern</button>
              <button id="md-detect" class="btn btn-ghost" type="button">Automatisch erkennen</button>
            </div>
          </section>
        </div>
      </main>
    </div>`;

  const root = ctx.contentEl.querySelector<HTMLElement>('.kf-page')!;
  const fit = () => { root.style.height = `${window.innerHeight}px`; };
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);
  fit();

  const input = ctx.contentEl.querySelector<HTMLInputElement>('#md-url')!;
  const hint = ctx.contentEl.querySelector<HTMLElement>('#md-hint')!;
  const derived = ctx.contentEl.querySelector<HTMLElement>('#md-derived')!;
  const saveBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#md-save')!;
  const detectBtn = ctx.contentEl.querySelector<HTMLButtonElement>('#md-detect')!;

  const embedding = readEmbeddingHost();
  detectBtn.disabled = embedding === null;
  if (embedding === null) {
    const grund = embeddingHostProblem(document.referrer, window.parent !== window);
    detectBtn.title = grund === 'technischer-host'
      ? 'Sie haben Vario unter seiner technischen Adresse geöffnet. Unter der liefert Vario '
        + 'keine Bilder aus. Bitte tragen Sie die Adresse ein, unter der Sie Vario sonst aufrufen.'
      : 'Nur möglich, wenn die App in Vario geöffnet ist';
  }

  void (async () => {
    const { data, error } = await ctx.supabase.rpc('kaufland_get_vario_public_base_url');
    if (error) {
      hint.textContent = `Konnte nicht geladen werden: ${error.message}`;
      return;
    }
    const row = Array.isArray(data) ? data[0] : null;
    input.value = row?.public_base_url ?? '';
    // Den abgeleiteten Host danebenstellen: Ohne den Vergleich sieht niemand, ob der
    // Eintrag ueberhaupt etwas aendert -- und genau diese Frage stand heute im Raum.
    if (row?.derived_base_url) {
      derived.textContent = row.public_base_url
        ? `Ohne Eintrag würde ${row.derived_base_url} verwendet.`
        : `Aktuell wird ${row.derived_base_url} verwendet (aus dem Zugangstoken abgeleitet).`;
    }
  })();

  detectBtn.addEventListener('click', () => {
    if (!embedding) return;
    input.value = embedding;
    hint.textContent = 'Erkannt. Zum Übernehmen speichern.';
  });

  saveBtn.addEventListener('click', () => {
    void (async () => {
      saveBtn.disabled = true;
      try {
        const { data, error } = await ctx.supabase.rpc('kaufland_set_vario_public_base_url', {
          _url: input.value.trim(),
        });
        if (error) {
          // Die Meldung der RPC durchreichen statt sie zu glaetten: "keine Vario-Cloud-Domain:
          // shop.example.de" sagt dem Haendler, WAS er falsch eingegeben hat.
          hint.textContent = error.message;
          showToast('Domain nicht gespeichert', 'error');
          return;
        }
        input.value = typeof data === 'string' ? data : '';
        hint.textContent = input.value
          ? 'Gespeichert. Neue Produktanlagen verwenden diese Adresse.'
          : 'Eintrag entfernt. Es gilt wieder die abgeleitete Adresse.';
        showToast('Domain gespeichert', 'success');
      } finally {
        saveBtn.disabled = false;
      }
    })();
  });
}
